<template>
  <button
    type="button"
    :class="[
      'inline-flex items-center gap-1 max-w-[180px] truncate text-[11px] leading-none px-2 py-1 rounded-full border transition-colors',
      kindClasses,
      removable ? 'cursor-pointer hover:opacity-80' : 'cursor-default opacity-90'
    ]"
    :title="label"
    :aria-label="label"
    :disabled="!removable"
    @click="onClick"
  >
    <span
      v-if="iconForKind"
      class="shrink-0"
      aria-hidden="true"
      >{{ iconForKind }}</span
    >
    <span class="truncate">{{ label }}</span>
    <span
      v-if="removable"
      class="shrink-0 ml-0.5 text-[10px] opacity-60 hover:opacity-100"
      aria-hidden="true"
      >×</span
    >
  </button>
</template>

<script lang="ts">
import { defineComponent, computed, inject } from 'vue'
import { useTheme } from '../../utils/useTheme'

export default defineComponent({
  name: 'ContextChip',
  props: {
    id: { type: String, required: true },
    label: { type: String, required: true },
    kind: { type: String, default: 'filter' },
    removable: { type: Boolean, default: true }
  },
  emits: ['remove'],
  setup(props, { emit }) {
    const theme = inject('theme', useTheme())
    const isDark = computed(() => theme.isDark.value)

    const iconForKind = computed(() => {
      switch (props.kind) {
        case 'route':
          return '📍'
        case 'cluster':
          return '🛢️'
        case 'project':
          return '🗂️'
        case 'dateRange':
          return '📅'
        case 'metric':
          return '📊'
        case 'rec':
          return '💡'
        default:
          return ''
      }
    })

    const kindClasses = computed(() => {
      // Subtle, theme-aware styles per chip kind so the row reads quickly.
      const dark = isDark.value
      switch (props.kind) {
        case 'route':
          return dark
            ? 'bg-blue-900/30 border-blue-700/50 text-blue-200'
            : 'bg-blue-50 border-blue-200 text-blue-700'
        case 'cluster':
          return dark
            ? 'bg-emerald-900/30 border-emerald-700/50 text-emerald-200'
            : 'bg-emerald-50 border-emerald-200 text-emerald-700'
        case 'project':
          return dark
            ? 'bg-indigo-900/30 border-indigo-700/50 text-indigo-200'
            : 'bg-indigo-50 border-indigo-200 text-indigo-700'
        case 'dateRange':
          return dark
            ? 'bg-amber-900/30 border-amber-700/50 text-amber-200'
            : 'bg-amber-50 border-amber-200 text-amber-700'
        case 'metric':
          return dark
            ? 'bg-purple-900/30 border-purple-700/50 text-purple-200'
            : 'bg-purple-50 border-purple-200 text-purple-700'
        case 'rec':
          return dark
            ? 'bg-rose-900/30 border-rose-700/50 text-rose-200'
            : 'bg-rose-50 border-rose-200 text-rose-700'
        default:
          return dark
            ? 'bg-gray-800 border-gray-600 text-gray-300'
            : 'bg-gray-100 border-gray-200 text-gray-700'
      }
    })

    function onClick() {
      if (props.removable) emit('remove', props.id)
    }

    return { isDark, iconForKind, kindClasses, onClick }
  }
})
</script>
